import { createRouter, createWebHistory } from "@ionic/vue-router";

import type {
  NavigationGuardWithThis,
  NavigationFailure,
  RouteRecordRaw,
  RouteLocationNamedRaw,
  Router,
  RouteRecordName,
  RouteLocationNormalizedLoaded,
} from "vue-router";

import { APP_ROUTES } from "@/Generated/Routes/paths";
import backMap from "@/Generated/Routes/backMap";

import { log } from "@/Utils/Debug";
import { isValidString } from "@/Utils/String";

export interface TcRouter extends Router {
  pushInternal: (to: RouteLocationNamedRaw) => Promise<void | NavigationFailure | undefined>;
  replaceInternal: (to: RouteLocationNamedRaw) => Promise<void | NavigationFailure | undefined>;
}
export class CustomRouter {
  private readonly routerInstance: TcRouter;

  constructor() {
    this.routerInstance = createRouter({
      history: createWebHistory(),
      routes: [
        {
          path: "/:catchAll(.*)",
          name: "splash",
          component: () => import("@/Views/Splash.vue"),
        },
      ],
    }) as TcRouter;

    this.routerInstance.pushInternal = this.routerInstance.push.bind(this.routerInstance);
    this.routerInstance.replaceInternal = this.routerInstance.replace.bind(this.routerInstance);
    this.routerInstance.back = this.goBack.bind(this);

    this.routerInstance.push = (to: RouteLocationNamedRaw): Promise<void | NavigationFailure | undefined> => {
      log(`Navigate to ${to.name as string} (PUSH)`);

      return this.routerInstance.pushInternal(to);
    };

    this.routerInstance.replace = (
      to: RouteLocationNamedRaw
    ): Promise<void | NavigationFailure | undefined> => {
      log(`Navigate to ${to.name as string} (REPLACE)`);

      return this.routerInstance.pushInternal(to);
    };
  }

  public addBeforeGuard(functionGuard: NavigationGuardWithThis<unknown>) {
    this.routerInstance.beforeResolve(functionGuard.bind(this));
  }

  public addRoute(route: RouteRecordRaw) {
    this.routerInstance.addRoute(route);
  }

  public removeRoute(routeName: RouteRecordName) {
    this.routerInstance.removeRoute(routeName!);
  }

  public hasRoute(routeName: RouteRecordName): boolean {
    return this.routerInstance.hasRoute(routeName!);
  }

  public get router() {
    return this.routerInstance;
  }

  public getInstance(): TcRouter {
    return this.routerInstance;
  }

  public isReady(): Promise<void> {
    return this.routerInstance.isReady();
  }

  public push(to: RouteLocationNamedRaw): Promise<void | NavigationFailure | undefined> {
    return this.getInstance().push(to);
  }

  public replace(to: RouteLocationNamedRaw | string): Promise<void | NavigationFailure | undefined> {
    return this.getInstance().push(to);
  }

  public currentRoute(): RouteLocationNormalizedLoaded {
    return this.router.currentRoute.value;
  }

  public goBack(): Promise<void | NavigationFailure | undefined> {
    const currentRoute = this.currentRoute();

    if (!isValidString(currentRoute.name)) {
      return this.push({ name: APP_ROUTES.root.name });
    }

    const backCandidate: string = (backMap as Record<string, string>)[currentRoute.name];

    if (!isValidString(backCandidate)) {
      return this.push({ name: APP_ROUTES.root.name });
    }

    return this.push({ name: backCandidate, params: currentRoute.params });
  }
}

const AppRouter = new CustomRouter();

export default AppRouter;
